import { useMemo } from "react";
import {
  TextField,
  DatagridConfigurable,
  DateField,
  NumberField,
  FunctionField,
  useTranslate,
  RecordContextProvider,
  ChipField,
  useTheme,
  usePermissions,
} from "react-admin";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import InventoryWriteOffExpandList from "../../components/inventoryWriteOffs/expand/InventoryWriteOffExpandList";
import InventoryWriteOffFooterList from "../../components/inventoryWriteOffs/footer/InventoryWriteOffFooterList";
import mutationInventoryWriteOffFilterList from "../../components/mutationInventoryWriteOffs/filter/mutationInventoryWriteOffFilterList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";
import ManualReportList from "../../components/reports/ManualReportList";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const InventoryWriteOffList = () => {
  const { permissions } = usePermissions();
  const t = useTranslate();
  const [theme] = useTheme("light");
  const { filterDefaultValues } = useCompanyScopedListFilters({
    start_date: firstDate,
    end_date: lastDate,
    approved: 1,
  });

  const list = useMemo(
    () => (
      <ManualReportList
        title="mine.list_title.mutation_inventory_write_off"
        empty={false}
        actions={<DownloadAction />}
        filters={mutationInventoryWriteOffFilterList(permissions)}
        queryOptions={{
          meta: {
            sort: [
              "mutation_inventory_write_offs.type ASC",
              "mutation_inventory_write_offs.code ASC",
            ],
          },
        }} // overide default sort
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date"]}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<InventoryWriteOffFooterList />} />}
          bulkActionButtons={false}
          expand={<InventoryWriteOffExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          <TextField source="company.alias" label="Depo" />
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <FunctionField
            textAlign="center"
            label="mine.label.type"
            render={(record) => {
              let rec = {};
              switch (record.type) {
                case 1:
                  rec.type = t("mine.label.stocktake");
                  rec.color = "primary";
                  break;
                case 2:
                  rec.type = t("mine.label.sample");
                  rec.color = "success";
                  break;
                case 3:
                  rec.type = t("mine.label.warehouse_broke");
                  rec.color = "error";
                  break;
                default: // case 4
                  rec.type = t("mine.label.transfer_between_depo");
                  rec.color = "warning";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="type" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
          <NumberField source="quantity" />
          <FunctionField
            source="total"
            textAlign="right"
            render={(record) => {
              return (
                <RecordContextProvider value={record}>
                  <NumberField
                    source="total"
                    title={
                      record.approved
                        ? ""
                        : t("mine.tooltip.total_inventory_write_off")
                    }
                  />
                </RecordContextProvider>
              );
            }}
          />

          <TextField source="note" label="mine.label.note" />
          <TextField source="creator_name" label="mine.label.created_by" />
          <DateField
            source="created_at"
            showTime
            label="mine.label.created_at"
          />
        </DatagridConfigurable>
      </ManualReportList>
    ),
    [theme, permissions, filterDefaultValues]
  );
  return list;
};

export default InventoryWriteOffList;
